/**
 * SMSNotificationManager Component
 * Admin interface for managing SMS notification settings
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  MessageSquare, Bell, Users, ShieldCheck, Save, RefreshCw,
  CheckCircle, XCircle, User, Store, Shield
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { API } from '../App';

const NOTIFICATION_TYPES = [
  {
    category: "Cycle de transfert",
    icon: MessageSquare,
    color: "text-blue-600",
    notifications: [
      { key: "transaction_created", label: "Demande créée", description: "Quand une nouvelle demande de transfert est créée" },
      { key: "transaction_confirmed", label: "Transfert confirmé", description: "Quand les deux parties ont confirmé" },
      { key: "payment_received", label: "Paiement reçu", description: "Quand le paiement est complété" },
      { key: "tickets_verified", label: "Billets vérifiés", description: "Quand les billets sont vérifiés par l'admin" },
      { key: "transaction_completed", label: "Transfert complété", description: "Quand le transfert est finalisé" },
      { key: "funds_released", label: "Fonds libérés", description: "Quand les fonds sont envoyés au vendeur" },
    ]
  },
  {
    category: "Rappels",
    icon: Bell,
    color: "text-amber-600",
    notifications: [
      { key: "confirmation_reminder", label: "Rappel de confirmation", description: "Rappel pour confirmer le transfert" },
      { key: "payment_reminder", label: "Rappel de paiement", description: "Rappel pour compléter le paiement" },
      { key: "event_day_wishes", label: "Souhaits avant l'événement", description: "Message de courtoisie 24h avant l'événement" },
      { key: "rating_request", label: "Demande d'évaluation", description: "Invitation à laisser une évaluation" },
    ]
  },
  {
    category: "Litiges & Alertes",
    icon: ShieldCheck,
    color: "text-red-600",
    notifications: [
      { key: "dispute_opened", label: "Litige ouvert", description: "Quand un litige est signalé" },
      { key: "dispute_resolved", label: "Litige résolu", description: "Quand un litige est résolu" },
      { key: "negative_rating_alert", label: "Alerte évaluation négative", description: "Alerte admin pour les évaluations 1-2★" },
    ]
  }
];

const RECIPIENT_OPTIONS = [
  { value: "buyer", label: "Acheteur", icon: User, color: "bg-blue-100 text-blue-700" },
  { value: "seller", label: "Vendeur", icon: Store, color: "bg-green-100 text-green-700" },
  { value: "admin", label: "Admin", icon: Shield, color: "bg-purple-100 text-purple-700" },
];

const SMSNotificationManager = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [smsStatus, setSmsStatus] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchSmsStatus();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/sms/notification-settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des paramètres');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSmsStatus = async () => {
    try {
      const response = await axios.get(`${API}/sms/status`);
      setSmsStatus(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setHasChanges(true);
  };

  const handleRecipientToggle = (notificationKey, recipientValue) => {
    const recipientsKey = `${notificationKey}_recipients`;
    const currentRecipients = settings[recipientsKey] || [];
    
    let newRecipients;
    if (currentRecipients.includes(recipientValue)) {
      newRecipients = currentRecipients.filter(r => r !== recipientValue);
    } else {
      newRecipients = [...currentRecipients, recipientValue];
    }
    
    setSettings(prev => ({
      ...prev,
      [recipientsKey]: newRecipients
    }));
    setHasChanges(true);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/sms/notification-settings`, settings);
      toast.success('Paramètres sauvegardés');
      setHasChanges(false);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const enableAll = () => {
    const newSettings = { ...settings };
    NOTIFICATION_TYPES.forEach(category => {
      category.notifications.forEach(notif => {
        newSettings[notif.key] = true;
      });
    });
    setSettings(newSettings);
    setHasChanges(true);
  };

  const disableAll = () => {
    const newSettings = { ...settings };
    NOTIFICATION_TYPES.forEach(category => {
      category.notifications.forEach(notif => {
        newSettings[notif.key] = false;
      });
    });
    setSettings(newSettings);
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const enabledCount = NOTIFICATION_TYPES.reduce((acc, cat) => 
    acc + cat.notifications.filter(n => settings?.[n.key]).length, 0
  );
  const totalCount = NOTIFICATION_TYPES.reduce((acc, cat) => acc + cat.notifications.length, 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h3 className="text-lg font-semibold">Notifications SMS</h3>
          <p className="text-sm text-gray-500">
            Configurez quelles notifications sont envoyées et à qui
          </p>
        </div>
        <div className="flex items-center gap-2">
          {smsStatus && (
            <Badge className={smsStatus.enabled ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
              {smsStatus.enabled ? (
                <><CheckCircle className="h-3 w-3 mr-1" /> Twilio actif</>
              ) : (
                <><XCircle className="h-3 w-3 mr-1" /> Twilio inactif</>
              )}
            </Badge>
          )}
        </div>
      </div>

      {/* Status Bar */}
      <Card className="bg-gradient-to-r from-brand-50 to-blue-50">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white rounded-full shadow-sm">
                <MessageSquare className="h-6 w-6 text-brand-600" />
              </div>
              <div>
                <p className="font-medium">{enabledCount} / {totalCount} notifications actives</p>
                <p className="text-sm text-gray-500">
                  {smsStatus?.from_number && `Numéro: ${smsStatus.from_number}`}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={enableAll}>
                Tout activer
              </Button>
              <Button variant="outline" size="sm" onClick={disableAll}>
                Tout désactiver
              </Button>
              <Button 
                size="sm" 
                onClick={saveSettings} 
                disabled={!hasChanges || saving}
                className="bg-brand-600 hover:bg-brand-700"
                data-testid="save-sms-settings-btn"
              >
                {saving ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Sauvegarder
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notification Categories */}
      <div className="space-y-4">
        {NOTIFICATION_TYPES.map((category) => {
          const CategoryIcon = category.icon;
          const enabledInCategory = category.notifications.filter(n => settings?.[n.key]).length;
          
          return (
            <Card key={category.category}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <CategoryIcon className={`h-5 w-5 ${category.color}`} />
                  {category.category}
                  <Badge variant="outline" className="ml-2">
                    {enabledInCategory}/{category.notifications.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {category.notifications.map((notification) => {
                  const isEnabled = settings?.[notification.key] ?? true;
                  const recipients = settings?.[`${notification.key}_recipients`] || [];
                  
                  return (
                    <div 
                      key={notification.key}
                      className={`p-4 rounded-lg border transition-all ${
                        isEnabled ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100 opacity-60'
                      }`}
                      data-testid={`notification-${notification.key}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Switch
                              checked={isEnabled}
                              onCheckedChange={() => handleToggle(notification.key)}
                              data-testid={`toggle-${notification.key}`}
                            />
                            <Label className="font-medium cursor-pointer" onClick={() => handleToggle(notification.key)}>
                              {notification.label}
                            </Label>
                          </div>
                          <p className="text-sm text-gray-500 ml-11">
                            {notification.description}
                          </p>
                        </div>
                      </div>
                      
                      {/* Recipients Selection */}
                      {isEnabled && (
                        <div className="mt-4 ml-11">
                          <p className="text-xs text-gray-500 mb-2">Destinataires:</p>
                          <div className="flex flex-wrap gap-2">
                            {RECIPIENT_OPTIONS.map((recipient) => {
                              // Skip admin option for non-admin notifications
                              if (recipient.value === "admin" && !notification.key.includes("alert")) {
                                return null;
                              }
                              // Skip buyer/seller for admin-only notifications
                              if (notification.key === "negative_rating_alert" && recipient.value !== "admin") {
                                return null;
                              }
                              
                              const RecipientIcon = recipient.icon;
                              const isSelected = recipients.includes(recipient.value);
                              
                              return (
                                <button
                                  key={recipient.value}
                                  onClick={() => handleRecipientToggle(notification.key, recipient.value)}
                                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                    isSelected 
                                      ? recipient.color + ' ring-2 ring-offset-1 ring-current'
                                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                  }`}
                                  data-testid={`recipient-${notification.key}-${recipient.value}`}
                                >
                                  <RecipientIcon className="h-3.5 w-3.5" />
                                  {recipient.label}
                                  {isSelected && <CheckCircle className="h-3 w-3" />}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Save Button (Mobile) */}
      {hasChanges && (
        <div className="fixed bottom-4 left-4 right-4 sm:hidden">
          <Button 
            className="w-full bg-brand-600 hover:bg-brand-700 shadow-lg"
            onClick={saveSettings}
            disabled={saving}
          >
            {saving ? (
              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Sauvegarder les modifications
          </Button>
        </div>
      )}
    </div>
  );
};

export default SMSNotificationManager;
